/**
 * @fileoverview Shared defaults and value types for the report canvas builder —
 *             extracted so the host page, canvas, and tests share one source.
 * @module ui-admin/components/ReportCanvasBuilder/reportDefaults
 *
 * Responsibilities: - DEFAULT_REPORT_CONFIG (was inlined in ReportBuilderPage).
 *                   - ReportFieldCatalogEntry — the field catalog shape the host
 *                     injects (structurally compatible with tenant-web's
 *                     FieldDefinition without a cross-app import).
 *                   - ReportCanvasSurface — the center-pane surface tab ids.
 * Dependencies:     - @firesquirrel/local-db (ReportConfig type only).
 * Architecture:     - Pure static data + types; no logic, no Dexie, no API.
 * Notes:            - Adding a field catalog entry is the host's job (its
 *                     reportDataService owns the real Dexie field catalog).
 */

import type { ReportConfig } from '@firesquirrel/local-db';

/**
 * @contract ReportFieldCatalogEntry
 * Purpose: One selectable field in the canvas field palette.
 * Fields:  field (dotted path e.g. 'cases.caseNumber'), label, type, category
 *          (collection group name), count? (live row count, optional).
 * Rules:   Structural — hosts build this from their reportDataService.
 */
export interface ReportFieldCatalogEntry {
  field: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'boolean' | string;
  category: string;
  count?: number;
}

/**
 * @contract ReportCanvasSurface
 * Purpose: The center-pane surface tabs on the canvas toolbar.
 * Fields: 'columns' | 'filters' | 'grouping'
 */
export type ReportCanvasSurface = 'columns' | 'filters' | 'grouping';

/**
 * @contract DEFAULT_REPORT_CONFIG
 * Purpose: The fallback reportConfig when a report definition has none.
 * Rules:   Shared by the host page and the canvas; do not duplicate inline.
 */
export const DEFAULT_REPORT_CONFIG: ReportConfig = {
  format: 'tabular',
  dataSources: [],
  columns: [],
  groupBy: [],
  subtotals: false,
  grandTotal: false,
};
